import { authHeader, accountHeader, BASE_URL, handleResponse, handleResponseBlob } from '@helpers/api';
import { ProductEvent } from '@constants/product-events';
import { captureLocationsImported, captureOnSuccess } from '@helpers/product-events';

export const companyService = {
    addCompany,
    updateCompany,
    deleteCompany,
    importGoogleCompanies,
    importGoogleCompaniesWithProgress,
    editProfile,
    deleteProfile,
    disconnectIntegration,
    getCompaniesCSV,
    uploadCompaniesCSV,
    updateCompaniesBulk,
    uploadCompanyImage,
    connectListingDirectories,
    bulkUpdateLocations,
};

function addCompany(locationData) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(typeof locationData === 'string' ? { placeId: locationData } : locationData),
    };

    return fetch(`${BASE_URL}api/locations/create`, requestOptions)
        .then(handleResponse)
        .then(
            captureOnSuccess(ProductEvent.LocationCreated, () => ({
                source: typeof locationData === 'string' ? 'google_places' : 'manual',
            })),
        );
}

function updateCompany(company) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(company),
    };

    return fetch(`${BASE_URL}api/locations/update`, requestOptions).then(handleResponse);
}

function editProfile(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/profiles/generic`, requestOptions).then(handleResponse);
}

function deleteProfile(companyId, profileId) {
    const requestOptions = {
        method: 'DELETE',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/profiles?companyId=${companyId}&profileId=${profileId}`, requestOptions).then(
        handleResponse,
    );
}

function deleteCompany(companyId) {
    const requestOptions = {
        method: 'DELETE',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/locations/${companyId}`, requestOptions).then(handleResponse);
}

function disconnectIntegration(locationId, integrationId) {
    const requestOptions = {
        method: 'DELETE',
        headers: authHeader(),
    };

    return fetch(
        `${BASE_URL}api/locations/${locationId}/integrations/${integrationId}/disconnect`,
        requestOptions,
    ).then(handleResponse);
}

function connectListingDirectories(locationId) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/locations/${locationId}/integrations/listing-directory:connect`, requestOptions)
        .then(handleResponse)
        .then(captureOnSuccess(ProductEvent.DirectoryListingConnected));
}

function importGoogleCompanies(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/google/import-locations`, requestOptions).then(handleResponse);
}

const SOFT_SKIP_REASONS = ['profile_connected_to_another_location'];

async function importGoogleCompaniesWithProgress(companies, onProgress) {
    let imported = 0;
    const skipped = [];
    const failed = [];

    const record = ({ name, reason }) => (SOFT_SKIP_REASONS.includes(reason) ? skipped : failed).push({ name, reason });

    for (let i = 0; i < companies.length; i += 1) {
        const company = companies[i];
        try {
            const result = await importGoogleCompanies([company]);
            imported += result?.importedCount ?? 0;
            (result?.skippedLocations || []).forEach(record);
        } catch (error) {
            record({ name: company.title || company.name, reason: error?.errorCode || 'import_error' });
        }
        onProgress?.(i + 1, companies.length);
    }

    captureLocationsImported({ source: 'google', count: imported });

    return { imported, skipped, failed };
}

function getCompaniesCSV() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/locations/bulk/export`, requestOptions).then(handleResponseBlob);
}

function uploadCompaniesCSV(data) {
    const user = JSON.parse(localStorage.getItem('user'));
    const requestOptions = {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + user.token,
            ...accountHeader(),
        },
        body: data,
    };

    return fetch(`${BASE_URL}api/locations/bulk/import`, requestOptions).then(handleResponse);
}

function updateCompaniesBulk(data) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/locations/bulk/run`, requestOptions)
        .then(handleResponse)
        .then(
            captureOnSuccess(ProductEvent.LocationsImported, () => ({
                source: 'csv',
                count: data.data.filter(location => !(location.companyId > 0)).length,
                updated: data.updated,
            })),
        );
}

function uploadCompanyImage(data, companyId, type) {
    const user = JSON.parse(localStorage.getItem('user'));
    const requestOptions = {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + user.token,
            ...accountHeader(),
        },
        body: data,
    };
    return fetch(`${BASE_URL}api/locations/${companyId}/upload-asset?type=${type}`, requestOptions).then(
        handleResponse,
    );
}

function bulkUpdateLocations(request) {
    const requestOptions = {
        method: 'PATCH',
        headers: authHeader(),
        body: JSON.stringify(request),
    };

    return fetch(`${BASE_URL}api/locations/bulk/update`, requestOptions).then(handleResponse);
}
